import type { Attachment } from '@medplum/fhirtypes'
import type { AttachmentConversionStatus } from '@seen/core/attachmentConversion'
import { getBinaryId, saveBlobWithName } from '@seen/core/utils'
import { Button } from '@seen/ui'
import { IconDownload } from '@tabler/icons-react'
import type { JSX, ReactNode } from 'react'
import { useCallback } from 'react'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

interface AttachmentCardLayoutProps {
  attachment: Attachment
  title: string
  icon: ReactNode
  preview?: ReactNode
  extraActions?: ReactNode
  onTitleChange?: (title: string) => void
  conversionStatus?: AttachmentConversionStatus | 'ready'
}

export function AttachmentCardLayout({
  attachment,
  title,
  icon,
  preview,
  extraActions,
  onTitleChange,
  conversionStatus,
}: AttachmentCardLayoutProps): JSX.Element {
  const medplum = useMedplum()
  const debouncedOnTitleChange = useDebouncedCallback(
    onTitleChange ?? (() => {}),
    1000
  )
  const isConversionPending = conversionStatus === 'pending'

  const download = useCallback(async (): Promise<void> => {
    if (!attachment.url) {
      return
    }
    const blob = await medplum.downloadAttachmentAsBinary(attachment.url)
    saveBlobWithName(blob, getBinaryId(attachment.url) ?? attachment.title)
  }, [attachment.url, attachment.title, medplum])

  const size = attachment.size
    ? `${(attachment.size / 1000).toFixed(0)} KB`
    : null

  const downloadName = attachment.title?.trim() || title
  const downloadLabel = downloadName ? `Download ${downloadName}` : 'Download'

  return (
    <div className="overflow-hidden rounded-lg border bg-muted">
      {preview && (
        <div className="relative bg-zinc-100 dark:bg-zinc-900">{preview}</div>
      )}
      <div
        className={`flex items-center gap-3 px-4 py-3 ${preview ? 'border-t' : ''}`}
      >
        <div className="shrink-0 text-muted-foreground">{icon}</div>
        <div className="min-w-0 flex-1">
          {onTitleChange ? (
            <RichTextEditor
              variant="unstyled"
              className="truncate text-sm font-medium"
              value={title}
              onChange={debouncedOnTitleChange}
            />
          ) : (
            <p className="truncate text-sm font-medium">{title}</p>
          )}
          {size && <p className="text-xs text-muted-foreground">{size}</p>}
        </div>
        <div className="flex shrink-0 gap-2">
          {extraActions}
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={download}
                  aria-label={downloadLabel}
                  disabled={isConversionPending}
                >
                  <IconDownload className="h-4 w-4" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {isConversionPending
                ? 'Available when conversion finishes'
                : downloadLabel}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
